import {
    Breakpoints,
    CTA,
    Image,
    Text,
    TextTag,
    styled,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {css} from 'styled-components'
// tslint:disable-next-line:no-duplicate-imports
import type {DefaultTheme} from 'styled-components'
import {boxedContainer, gridUtils} from '../../../assets/css-vars/gridUtils'
import WhiteButton from '../../../assets/css-vars/whiteButton'
import TrackingAction from '../../../tracking-manager'
import {useFeatureServices} from '../../../use-feature-services'
import {getVehiclePromotionsButtonClickData} from '../../../utils/tracking/getTrackingData'
import resolveCtaLabel from '../../../utils/text/resolveCtaLabel'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import {RootState} from '../../rootReducer'

const showroomBg = css`
    position: relative;

    &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: ${(props) => props.theme.colors.gradient.top};
    }
`

const ShowroomCopy = css`
    position: relative;
`

const ShowroomCopyDesktop = styled.div`
    ${ShowroomCopy}

    color: ${({theme}: {theme: DefaultTheme}) => theme.colors.content.inverted};
    display: none;
    line-height: 24px;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        display: block;
    }
`

const bgWrapper = css`
    ${showroomBg};
    padding-top: ${gridUtils.gap};
    padding-right: ${gridUtils.gap};
    padding-left: ${gridUtils.gap};
`

const ShowroomCopyMobileContainer = styled.div`
    color: ${({theme}: {theme: DefaultTheme}) => theme.colors.content.inverted};
    padding-right: ${gridUtils.gap};
    padding-left: ${gridUtils.gap};

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        display: none;
    }
`

const ShowRoomContainer = styled.section`
    ${boxedContainer}

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        display: grid;
        grid-template-columns: 40% 1fr;
        min-height: 400px;
    }

    @media only screen and (min-width: ${Breakpoints.b1280}px) {
        min-height: 575px;
    }
`

const ShowRoomTextContainer = styled.div`
    ${bgWrapper}

    background: ${({theme}: {theme: DefaultTheme}) =>
        theme.colors.gradient.top};
    padding-bottom: ${gridUtils.gap};

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        padding-right: 50px;
    }

    @media only screen and (min-width: ${Breakpoints.b1280}px) {
        padding-top: ${gridUtils.gapDouble};
        padding-right: 180px;
        padding-left: ${gridUtils.gapDouble};
    }
`

const ShowroomTitle = styled(Text)`
    color: ${({theme}: {theme: DefaultTheme}) => theme.colors.content.inverted};
    position: relative;
    margin-bottom: 25px;

    @media only screen and (min-width: ${Breakpoints.b560}px) {
        margin-bottom: 45px;
    }
`

const ShowroomImageWrapper = styled.div`
    ${bgWrapper}

    margin-bottom: 20px;

    &::before {
        max-height: 70%;

        @media (min-width: ${Breakpoints.b560}px) {
            content: none;
        }
    }

    @media (min-width: ${Breakpoints.b560}px) {
        padding: 0;
        margin: 0;
        align-self: center;
    }
`

const ShowroomImage = styled(Image)`
    --image-gap: 30px;

    position: relative;
    width: calc(100% + var(--image-gap));
    border-radius: 16px;

    // divide by 2 and convert to a negative number
    margin-left: calc(var(--image-gap) / 2 * -1);

    @media (min-width: ${Breakpoints.b960}px) {
        --image-gap: 40px;

        margin-left: calc(var(--image-gap) * -1);
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        --image-gap: 120px;

        width: 840px;
    }
`

const ShowroomCTAContainer = styled.div`
    @media (min-width: ${Breakpoints.b560}px) {
        margin-top: 50px;
    }

    a {
        ${WhiteButton}
    }
`

export interface ShowroomBannerInterface {
    modelName: string
    showroomImage: string | null
    showroomUrl: string | null
    showroomCtaLabel: string | null
    showroomDescription: string | null
    modelSlug: string
}

export default function ShowroomBanner(
    props: ShowroomBannerInterface
): React.JSX.Element {
    const {t} = useTranslation('app')
    const {tracking: trackingManager} = useFeatureServices()
    const showroomCtaText = resolveCtaLabel(
        props.showroomCtaLabel,
        t('cta.singlePromotion.showroomCta')
    )
    const showroomUrl = props.showroomUrl?.trim()
    const showroomDescription = props.showroomDescription?.trim()
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )

    return (
        <ShowRoomContainer>
            <ShowRoomTextContainer>
                <ShowroomTitle
                    tag={TextTag.h2}
                    appearance={TokenTextAppearance.headline300}
                    bold
                >
                    {props.modelName}
                </ShowroomTitle>

                {showroomDescription ? (
                    <ShowroomCopyDesktop
                        dangerouslySetInnerHTML={{
                            __html: showroomDescription,
                        }}
                    />
                ) : undefined}

                {showroomUrl ? (
                    <ShowroomCTAContainer>
                        <CTA
                            tag="a"
                            emphasis="secondary"
                            href={showroomUrl}
                            aria-label={showroomCtaText}
                            onClick={() => {
                                if (checkTrackingManager(trackingManager)) {
                                    const clickData =
                                        getVehiclePromotionsButtonClickData(
                                            showroomCtaText,
                                            props.modelName,
                                            props.modelSlug,
                                            trackingBrand,
                                            isShowroom
                                        )

                                    setTracking(
                                        trackingManager,
                                        TrackingAction.VWBasic_VehiclePromotionsButton_Click,
                                        clickData
                                    )
                                }
                            }}
                        >
                            {showroomCtaText}
                        </CTA>
                    </ShowroomCTAContainer>
                ) : undefined}
            </ShowRoomTextContainer>

            {props.showroomImage ? (
                <ShowroomImageWrapper>
                    <ShowroomImage
                        src={props.showroomImage}
                        alt={props.modelName}
                    />
                </ShowroomImageWrapper>
            ) : undefined}

            {showroomDescription ? (
                <ShowroomCopyMobileContainer
                    dangerouslySetInnerHTML={{
                        __html: showroomDescription,
                    }}
                />
            ) : undefined}
        </ShowRoomContainer>
    )
}
